import React, { useEffect, useState } from "react";
import {
  useOrderByIdQuery,
  usePaymentByIdQuery,
} from "../../../Store/Apis/bookingApi";
import { useLocation } from "react-router-dom";
import { useTaxPercentageQuery } from "../../../Store/Apis/toolsApi";
import { useSelector } from "react-redux";
import formatDate from "../../../Hooks/formatDate";

const PrintInvoice = () => {
  const [logoLoaded, setLogoLoaded] = useState(false); // Track logo loading state
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get("id");

  const orderByIdApi = useOrderByIdQuery(
    {
      orderID: orderId,
      outletID: user?.outletID,
    },
    { skip: !orderId || !user?.outletID }
  );

  const paymentByIdApi = usePaymentByIdQuery({
    orderId: orderId,
  });
  const taxPercentageApi = useTaxPercentageQuery();

  // No tax calculations - tax disabled
  const currentOrderData = orderByIdApi?.data?.data[0];
  const taxPercentage = 0;
  const taxAmount = 0;
  const grossTotal =
    Number(currentOrderData?.subTotal) -
    Number(currentOrderData?.discount || 0);

  useEffect(() => {
    const sidebar = document.querySelector("aside.sidenav");
    const mainContent = document.querySelector(
      ".sidenav.fixed-start + .main-content"
    );
    const navbar = document.querySelector(".navbar-main");

    if (sidebar) sidebar.style.display = "none";
    if (mainContent) mainContent.style.marginLeft = "0";
    if (navbar) navbar.style.display = "none";
  }, []);

  useEffect(() => {
    if (orderByIdApi.isSuccess && logoLoaded) {
      // Open print dialog only after logo has loaded
      window.print();

      // Close the page when the print dialog is closed
      const handleAfterPrint = () => {
        window.close();
      };

      window.addEventListener("afterprint", handleAfterPrint);

      return () => {
        window.removeEventListener("afterprint", handleAfterPrint);
      };
    }
  }, [orderByIdApi.isSuccess, logoLoaded]);

  if (!orderByIdApi.isSuccess || !orderByIdApi.data?.data?.[0]) {
    return null;
  }

  const orderData = orderByIdApi.data.data[0];

  return (
    <div className="card" id="invoice">
      <div className="card-body p-3">
        <div className="page-wrapper">
          <table className="table table-borderless">
            <thead>
              <tr>
                <td colSpan="3">
                  <img
                    src={user?.logo}
                    alt="Logo"
                    height={70}
                    className="mb-2"
                    onLoad={() => setLogoLoaded(true)} // Mark logo as loaded
                  />
                  <h5 className="text-uppercase fw-600 mb-1">
                    {user?.outletName || "Outlet Name"}
                  </h5>
                  {user?.outletID === 4 ? (
                    <>
                      <p className="mb-0">066 520 8802 / 017 1705 220 </p>
                      <p className="mb-0">
                        waterfront.reservations2@gscollege.edu.za
                      </p>
                      <p className="mb-0">
                        Portion 9, Plaas Verblyden, Standerton, 2430
                      </p>
                    </>
                  ) : user?.outletID === 3 ? (
                    <>
                      <p className="mb-0">+27 17 714 0039</p>
                      <p className="mb-0">
                        isiqalo.reservations1@gscollege.edu.za
                      </p>
                      <p className="mb-0">5A Kieser Street, Standerton</p>
                    </>
                  ) : (
                    <>
                      <p className="mb-0">
                        {user?.outletPhone || "Phone Number"}
                      </p>
                      <p className="mb-0">{user?.outletAddress || "Address"}</p>
                    </>
                  )}
                </td>
                <td colSpan="2" className="text-end">
                  {(orderData.companyName || orderData.customerName) && (
                    <div className="fw-bold mb-2">
                      {(
                        orderData.companyName || orderData.customerName
                      ).toUpperCase()}
                    </div>
                  )}
                  <h6 className="fw-bold">
                    <span>Order ID -</span>
                    <span className="ms-2">{orderData.orderNumber}</span>
                  </h6>
                  {orderData.services?.some(
                    (service) => service.serviceId === 251
                  ) ? (
                    <p className="mb-1">
                      <span>Event Date:</span>
                      <span className="fw-600 ms-2">
                        {formatDate(orderData.orderDate)}
                      </span>
                    </p>
                  ) : (
                    <>
                      <p className="mb-1">
                        <span>Check In Date:</span>
                        <span className="fw-600 ms-2">
                          {formatDate(orderData.orderDate)}
                        </span>
                      </p>
                      <p className="mb-1">
                        <span>Check Out Date:</span>
                        <span className="fw-600 ms-2">
                          {formatDate(orderData.checkOutDate)}
                        </span>
                      </p>
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan="5" className="text-center">
                  <h4 className="text-uppercase my-3">Invoice</h4>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-light">
                <th className="text-uppercase text-secondary text-sm text-dark px-3">
                  #
                </th>
                <th className="text-uppercase text-secondary text-sm text-dark">
                  Product/Service Name
                </th>
                <th className="text-uppercase text-secondary text-sm text-dark">
                  Rate
                </th>
                <th className="text-uppercase text-secondary text-sm text-dark">
                  QTY
                </th>
                <th className="text-uppercase text-secondary text-sm text-dark">
                  Total
                </th>
              </tr>
              {orderData.services?.map((service, index) => (
                <tr key={service.serviceId || index}>
                  <td className="px-3">{index + 1}</td>
                  <td>
                    <div className="d-flex flex-column">
                      <h6 className="mb-0">
                        {service.service_type_name ||
                          service.serviceName ||
                          "Service"}
                      </h6>
                      <p className="mb-0 text-sm">
                        {service.productServiceName}
                      </p>
                      {service.roomNo && (
                        <p className="mb-0 text-xs text-muted">
                          Room: {service.roomNo}
                        </p>
                      )}
                      {service.subCustomerName && (
                        <p className="mb-0 text-xs text-muted">
                          Guest: {service.subCustomerName}
                        </p>
                      )}
                    </div>
                  </td>
                  <td>R {Number(service.servicePrice || 0).toFixed(2)}</td>
                  <td>{service.serviceQuantity || 0}</td>
                  <td>
                    R {Number(service.service_detail_total || 0).toFixed(2)}
                  </td>
                </tr>
              )) || []}
              <tr>
                <td colSpan="3">
                  <h6 className="fw-500">Invoice To:</h6>
                  <p style={{ marginBottom: 3 }}>{orderData.customerName}</p>
                  <p style={{ marginBottom: 3 }}>{orderData.customerNumber}</p>
                  <p style={{ marginBottom: 3 }}>{orderData.emails}</p>
                  <p style={{ marginBottom: 3 }}>{orderData.addresses}</p>
                  <p>GST No: {}</p>
                </td>
                <td colSpan="2" className="align-top">
                  <h6 className="fw-500 mb-2">Payment Details:</h6>
                  <div className="row mb-50 align-items-center">
                    <div className="col text-sm">Sub Total:</div>
                    <div className="col-auto text-sm">
                      R {orderData.subTotal.toFixed(2)}
                    </div>
                  </div>
                  <div className="row mb-50 align-items-center">
                    <div className="col text-sm">Discount:</div>
                    <div className="col-auto text-sm">
                      R {orderData.discount.toFixed(2)}
                    </div>
                  </div>
                  <div className="row align-items-center">
                    <div className="col text-sm fw-600">Total:</div>
                    <div className="col-auto text-sm text-dark fw-600">
                      R {grossTotal.toFixed(2)}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="fw-bold">
                      <span>Paid Amount: </span>
                      <span>R {Number(orderData.amountPaid).toFixed(2)}</span>
                    </div>
                    <div className="fw-bold">
                      <span>Due Amount: </span>
                      <span>
                        R{" "}
                        {(
                          Number(grossTotal) - Number(orderData.amountPaid)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <h6 className="mb-3 fw-500">Payments</h6>
          {paymentByIdApi?.data?.data.length > 0 ? (
            <div class="timeline timeline-one-side">
              {paymentByIdApi?.data?.data?.map((elem) => {
                return (
                  <div class="timeline-block mb-3">
                    <span class="timeline-step">
                      <i class="fa fa-dot-circle-o text-secondary"></i>
                    </span>
                    <div class="timeline-content">
                      <h6 class="text-dark text-sm font-weight-bold mb-0">
                        R {elem.amount.toFixed(2)}
                      </h6>
                      <p class="text-secondary text-xs mt-1 mb-0">
                        <span>{formatDate(elem.paymentDate)}</span>
                        <span class="ms-2 fw-600 text-uppercase">
                          {elem.paymentTypeName && `[${elem.paymentTypeName}]`}
                        </span>
                      </p>
                      <p class="text-secondary text-xs mt-1 mb-0 text-italic">
                        <span>UTR No -</span>
                        <span class="ms-2 fw-600 text-uppercase">
                          {elem.utrNumber}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm">No Payment Made Yet !</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintInvoice;
